import { useState, useEffect } from "react";
import React from "react";
import { Box, Paper, Typography, Button, MobileStepper, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { sliderData } from "./slider-data";

const SliderContainer = styled(Paper)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 0,
  [theme.breakpoints.down("sm")]: {
    height: "40vh" // for portrait mode or smaller screens
  },
  "@media (orientation: landscape)": {
    //height: "60vh" // Adjust height for landscape mode if needed
    height: "80vh"
  }
}));

const SlideImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover"
});

const SlideContent = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  textAlign: "center",

  color: "#fff",
  padding: theme.spacing(2),
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  borderRadius: theme.spacing(1),
  maxWidth: "80%",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    "& h2": {
      fontSize: "1.5rem"
    },
    "& p": {
      fontSize: "0.875rem"
    },
    "& button": {
      fontSize: "0.875rem",
      padding: theme.spacing(0.5, 1.5)
    },
    //test
    height: "60%"
  }
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "rgba(255, 255, 255, 0.3)",
  color: "white",
  padding: theme.spacing(2),
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.5)"
  },
  zIndex: 1,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    "& svg": {
      fontSize: "1.5rem"
    }
  }
}));

const Slider = ({ setSliderVisible, setSliderOff }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const slideLength = sliderData.length;

  const autoScroll = true;
  let slideInterval;
  let intervalTime = 5000;

  const handleNext = () => {
    setCurrentSlide((prevSlide) => (prevSlide === slideLength - 1 ? 0 : prevSlide + 1));
  };

  const handleBack = () => {
    setCurrentSlide((prevSlide) => (prevSlide === 0 ? slideLength - 1 : prevSlide - 1));
  };

  const handleClick = () => {
    setSliderVisible(false);
    setSliderOff(true);
  };

  useEffect(() => {
    setCurrentSlide(0);
  }, []);

  useEffect(() => {
    if (autoScroll) {
      slideInterval = setInterval(handleNext, intervalTime);
    }
    return () => clearInterval(slideInterval);
  }, [currentSlide]);

  const currentSlideData = sliderData.find((slide) => slide.id === currentSlide + 1);
  // Use MediaQuery to check if the screen is large enough or in landscape orientation
  const isLandscape = useMediaQuery("(orientation: landscape)");
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md")); // 'md' is for medium and larger screens

  if (!currentSlideData) return null;

  return (
    <SliderContainer elevation={3}>
      <Box sx={{ height: "100%", position: "relative" }}>
        <NavigationButton
          onClick={handleBack}
          sx={{
            left: { xs: 0, sm: theme.spacing(2) },
            borderRadius: { xs: 0, sm: "50%" }
          }}
          size="large"
        >
          <KeyboardArrowLeft fontSize="large" />
        </NavigationButton>

        <NavigationButton
          onClick={handleNext}
          sx={{
            right: { xs: 0, sm: theme.spacing(2) },
            borderRadius: { xs: 0, sm: "50%" }
          }}
          size="large"
        >
          <KeyboardArrowRight fontSize="large" />
        </NavigationButton>

        <SlideImage src={currentSlideData.image} alt={currentSlideData.heading} />
        <SlideContent>
          <Typography variant="h5" component="h5" gutterBottom>
            {currentSlideData.heading}
          </Typography>

          {/* Only show description if landscape orientation or large screen */}
          {(isLandscape || isLargeScreen) && (
            <Typography variant="body1" paragraph>
              {currentSlideData.desc}
            </Typography>
          )}
          <Button variant="contained" color="primary" size="large" onClick={handleClick} sx={{ mt: 2 }}>
            Get Started
          </Button>
        </SlideContent>
      </Box>
      <MobileStepper
        steps={slideLength}
        position="bottom"
        activeStep={currentSlide}
        backButton={null}
        nextButton={null}
        sx={{
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          position: "absolute",
          bottom: 0,
          width: "100%",
          justifyContent: "center",
          "& .MuiMobileStepper-dot": {
            backgroundColor: "rgba(0, 0, 0, 0.3)"
          },
          "& .MuiMobileStepper-dotActive": {
            backgroundColor: theme.palette.primary.main
          }
        }}
      />
    </SliderContainer>
  );
};

export default Slider;
